import React, { useContext } from 'react'
import { observer } from 'mobx-react'
import getCatalogPath from '@App/helpers/work-entities/utils/get-catalog-path'
import ExtendedWorkItem from '@Components/WorkItemV2/ExtendedWorkItem'
import CustomToolTip from '@Components/Tooltips/CustomToolTip'
import ShareDisplay from '@Components/WorkItemV2/ShareDisplay'
import RadioSelect from '@Components/Selectors/RadioSelect'
import Button from '@Components/Buttons/Button'
import { getCatalogWorkFields } from '@Components/WorkItemV2/Catalog/CatalogExtendedWorkItem/get-catalog-work-fields'
import RootStore from '@App/stores/root-store'
import { StoresContext } from '@App/contexts'
import { WorkModel } from '@Entities/work/work'
import { Maybe } from '@Types'
import { ICONS } from '@Const/common'
import './index.scss'

interface WorkMergeCandidateProps {
    work: WorkModel
    yourShares?: Maybe<number>
    useRadioSelect?: boolean
    onRemoval?(id: number): void
}

const WorkMergeCandidate = ({ work, yourShares, useRadioSelect, onRemoval }: WorkMergeCandidateProps) => {
    const { rightsholderStore, catalogSearchBulkActionStore } = useContext<RootStore>(StoresContext)

    if (!work.id) {
        return null
    }

    const workId = work.id

    const totalSharesTooltipText =
        work.totalKnownShares < 100
            ? 'Part of these shares is unknown, so the total is less than 100%.'
            : 'This reflects total claimed shares on this work by all parties.'

    const catalogPath = getCatalogPath(work.isComplete, rightsholderStore.currentRightsholderId, work.id)

    const setPrimaryWorkMergeId = (id: number | null | undefined) => {
        if (!id) {
            return null
        }
        return catalogSearchBulkActionStore.setPrimaryWorkMergeId(id)
    }

    return (
        <div className="work-merge-candidate-wrapper">
            {onRemoval && (
                <Button
                    className="work-merge-candidate-remove-button"
                    mainColor
                    type="tertiary"
                    onlyIcon
                    icon={ICONS.trashCan}
                    iconAltText="Remove"
                    title="Remove"
                    onClick={() => onRemoval(workId)}
                />
            )}
            <ExtendedWorkItem
                work={work}
                fields={getCatalogWorkFields(work)}
                catalogPath={catalogPath}
                showOverclaimTooltip
                isBulkActionStarted
                selectionControl={
                    useRadioSelect && (
                        <RadioSelect
                            id={work.id.toString()}
                            checked={work.id === catalogSearchBulkActionStore.primaryWorkMergeId}
                            onClick={() => setPrimaryWorkMergeId(work.id)}
                        />
                    )
                }
            >
                {yourShares && (
                    <ShareDisplay
                        share={yourShares}
                        tooltip={
                            <CustomToolTip
                                title="Your Shares:"
                                info="This represents the total shares you have claimed on this work."
                            />
                        }
                    />
                )}

                <ShareDisplay
                    share={work.totalKnownShares}
                    highlight
                    tooltip={<CustomToolTip title="Total Known Shares:" info={totalSharesTooltipText} />}
                />
            </ExtendedWorkItem>
        </div>
    )
}

export default observer(WorkMergeCandidate)
